import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../Icon/Icon';
import './MenuCircular.scss';

const DRAG_THRESHOLD = 6;
const STORAGE_KEY = "solimat.fabPos";

const FAN_3 = {
  left:  [-135, 180, 135],
  right: [-45,   0,  45],
  up:    [-135, -90, -45],
  down:  [ 135,  90,  45],
};
const FAN_2 = {
  left:  [-150, 150],
  right: [ -30,  30],
  up:    [-120, -60],
  down:  [ 120,  60],
};

const MenuCircular = ({ countSurvey = 0, countNotices = 0 }) => {
  const [open, setOpen]       = useState(false);
  const [pos, setPos]         = useState(null);
  const [dragging, setDragging] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const fabRef  = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, baseLeft: 0, baseTop: 0, moved: false, pointerId: null });

  const navigate    = useNavigate();
  const location    = useLocation();
  const currentPath = location.pathname;

  const totalUnread = (countSurvey || 0) + (countNotices || 0);

  // Escuchar cambios del estado del tour
  useEffect(() => {
    const handler = (e) => setTourActive(e.detail?.active ?? false);
    window.addEventListener('solimat:tourStateChange', handler);
    return () => window.removeEventListener('solimat:tourStateChange', handler);
  }, []);

  // Restaurar posición guardada
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && typeof p.left === "number" && typeof p.top === "number") setPos(p);
      }
    } catch (_) {}
  }, []);

  // Cerrar al click fuera o Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (fabRef.current && !fabRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Mantener dentro del viewport al redimensionar
  useEffect(() => {
    const onResize = () => {
      if (!pos || !fabRef.current) return;
      const { width, height } = fabRef.current.getBoundingClientRect();
      const left = Math.max(8, Math.min(pos.left, window.innerWidth  - width  - 8));
      const top  = Math.max(8, Math.min(pos.top,  window.innerHeight - height - 8));
      if (left !== pos.left || top !== pos.top) setPos({ left, top });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  // --- Navegación ---------------------------------------------------------
  const DELTA_SEARCH_PATHS = [
    '/unidad-delta/accidentes-con-baja-pendientes-de-delta',
    '/unidad-delta/accidentes-con-baja-delta-remesados',
    '/unidad-delta/accidentes-sin-baja-pendientes-delta',
    '/unidad-delta/accidentes-sin-baja-delta-remesados',
  ];

  const showButtonGeneralHelp = () => {
    if (currentPath === '/' || currentPath === '/inicio') return true;
    if (currentPath.match(/^\/historial\/\d+(_\w+)?/)) return true;
    if (currentPath.match(/^\/comunicaciones\/.+\/comunicacion-general/)) return true;
    if (['/mis-citas', '/mis-gestiones', '/notificaciones', '/comunicaciones', '/historial', '/unidad-delta', '/red-de-centros', ...DELTA_SEARCH_PATHS].includes(currentPath)) return true;
    return false;
  };

  const handleEncuestas = () => { setOpen(false); navigate('/mis-encuestas'); };
  const handleAvisos    = () => { setOpen(false); navigate('/avisos'); };
  const handleAyuda = () => {
    setOpen(false);
    if (currentPath === '/' || currentPath === '/inicio') {
      window.dispatchEvent(new CustomEvent('solimat:startHomeTour'));
    } else if (currentPath === '/unidad-delta') {
      window.dispatchEvent(new CustomEvent('solimat:startDeltaTour'));
    } else if (DELTA_SEARCH_PATHS.includes(currentPath)) {
      window.dispatchEvent(new CustomEvent('solimat:startDeltaSearchTour'));
    } else if (currentPath === '/mis-citas') {
      window.dispatchEvent(new CustomEvent('solimat:startAppointmentsTour'));
    } else if (currentPath === '/mis-gestiones') {
      window.dispatchEvent(new CustomEvent('solimat:startManagementTour'));
    } else if (currentPath === '/historial') {
      window.dispatchEvent(new CustomEvent('solimat:startRecordTour'));
    } else if (currentPath.match(/^\/historial\/\d+(_\w+)?/)) {
      window.dispatchEvent(new CustomEvent('solimat:startRecordDetailTour'));
    } else if (currentPath === '/notificaciones') {
      window.dispatchEvent(new CustomEvent('solimat:startNotificationsTour'));
    } else if (currentPath === '/comunicaciones') {
      window.dispatchEvent(new CustomEvent('solimat:startCommunicationsTour'));
    } else if (currentPath.match(/^\/comunicaciones\/.+\/comunicacion-general/)) {
      window.dispatchEvent(new CustomEvent('solimat:startCommunicationChatTour'));
    } else if (currentPath === '/red-de-centros') {
      window.dispatchEvent(new CustomEvent('solimat:startCentersTour'));
    } else {
      navigate(`/ayuda-general?from=${encodeURIComponent(location.pathname)}`);
    }
  };

  // --- Drag ---------------------------------------------------------------
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const rect = fabRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseLeft: rect.left, baseTop: rect.top, moved: false, pointerId: e.pointerId };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (d.pointerId == null) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      d.moved = true;
      setDragging(true);
      setOpen(false);
    }
    if (!d.moved) return;
    const { width, height } = fabRef.current.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(d.baseLeft + dx, window.innerWidth  - width  - 8)),
      top:  Math.max(8, Math.min(d.baseTop  + dy, window.innerHeight - height - 8)),
    });
  };

  const onPointerUp = (e) => {
    const d = dragRef.current;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    if (d.moved) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch (_) {}
      setDragging(false);
    } else {
      setOpen(o => !o);
    }
    dragRef.current = { startX: 0, startY: 0, baseLeft: 0, baseTop: 0, moved: false, pointerId: null };
  };

  // --- Auto-flip del abanico según espacio disponible --------------------
  let quadrant = "left";
  if (pos && fabRef.current) {
    const rect = fabRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width  / 2;
    const cy = rect.top  + rect.height / 2;
    const spaces = { left: cx, right: window.innerWidth - cx, up: cy, down: window.innerHeight - cy };
    quadrant = Object.entries(spaces).reduce((a, b) => (b[1] > a[1] ? b : a))[0];
  }

  const showHelp = showButtonGeneralHelp();
  const isHomePage = currentPath === '/' || currentPath === '/inicio';
  const fan      = showHelp ? FAN_3[quadrant] : FAN_2[quadrant];

  const baseOptions = [
    { id: "encuestas", label: "Encuestas", count: countSurvey,  onClick: handleEncuestas },
    { id: "avisos",    label: "Avisos",    count: countNotices, onClick: handleAvisos },
    ...(showHelp ? [{ id: "ayuda", label: "Ayuda", count: 0, onClick: handleAyuda }] : []),
  ];

  const options = baseOptions.map((opt, i) => {
    const rad = (fan[i] * Math.PI) / 180;
    const radius = getComputedStyle(document.documentElement).getPropertyValue('--fab-radius').trim() || '120';
    const r = parseFloat(radius);
    return { ...opt, dx: Math.cos(rad) * r, dy: Math.sin(rad) * r };
  });

  const customStyle = pos
    ? { left: `${pos.left}px`, top: `${pos.top}px`, right: "auto", bottom: "auto" }
    : undefined;

  return (
    <div
      ref={fabRef}
      className={["fab", open ? "fab--open" : "", dragging ? "fab--dragging" : "", pos ? "fab--placed" : ""].filter(Boolean).join(" ")}
      style={customStyle}
      aria-live="polite"
    >
      <span className="fab__ring" aria-hidden="true" />

      <ul className="fab__menu" role="menu" aria-label="Información y avisos" aria-hidden={!open}>
        {options.map((opt) => (
          <li
            key={opt.id}
            className="fab__item"
            style={{ "--dx": `${opt.dx.toFixed(1)}px`, "--dy": `${opt.dy.toFixed(1)}px` }}
          >
            <button
              className={`fab__opt fab__opt--${opt.id}`}
              role="menuitem"
              tabIndex={open ? 0 : -1}
              onClick={opt.onClick}
              aria-label={opt.label + (opt.count ? ` (${opt.count} sin leer)` : "")}
            >
              <span className="fab__opt-label">{opt.label}</span>
              {opt.count > 0 && <span className="fab__opt-badge" aria-hidden="true">{opt.count}</span>}
            </button>
          </li>
        ))}
      </ul>

      <span className="fab__tour-anchor" aria-hidden="true" />

      <button
        className="fab__main"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={open ? "Cerrar menú de información" : "Abrir menú de información (arrastra para mover)"}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className="fab__main-glyph" aria-hidden="true">
          <Icon name={open ? "x" : "info"} size={22} strokeWidth={2.2} />
        </span>
        {totalUnread > 0 && !open && <span className="fab__alert" aria-hidden="true">!</span>}
        <span className="fab__drag-hint" aria-hidden="true">
          <svg width="14" height="6" viewBox="0 0 14 6" fill="currentColor">
            <circle cx="2"  cy="2" r="1" /><circle cx="7"  cy="2" r="1" /><circle cx="12" cy="2" r="1" />
            <circle cx="2"  cy="5" r="1" /><circle cx="7"  cy="5" r="1" /><circle cx="12" cy="5" r="1" />
          </svg>
        </span>
      </button>
    </div>
  );
};

export default MenuCircular;
